/**
 * Helpers for serialising an inking raster to/from a compact PNG data URL.
 *
 * The editable raster lives in memory as a Uint8ClampedArray (RGBA, premultiplied
 * by opacity during stamping). For persistence and sidebar thumbnails we round-
 * trip through a Canvas 2D context: `rasterToPng` draws the buffer into an
 * offscreen canvas and calls `toDataURL('image/png')`; `pngToRaster` reverses
 * the process asynchronously via an Image decode.
 *
 * PNG compression handles the dominant transparent/uniform regions in line art
 * well — a 2k × 2k stroke raster typically drops from 16 MB raw to well under
 * 1 MB encoded.
 */

export function rasterToPng(
  raster: Uint8ClampedArray,
  width: number,
  height: number,
): string {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable");
  const imgData = ctx.createImageData(width, height);
  imgData.data.set(raster);
  ctx.putImageData(imgData, 0, 0);
  return canvas.toDataURL("image/png");
}

/**
 * Downscale the raster and encode it as PNG for the sidebar thumbnail.
 * `maxWidth` is the target long-edge in pixels; aspect ratio is preserved.
 */
export function rasterToThumbnailPng(
  raster: Uint8ClampedArray,
  width: number,
  height: number,
  maxWidth: number,
): string {
  const scale = Math.min(1, maxWidth / Math.max(width, height));
  const tw = Math.max(1, Math.round(width * scale));
  const th = Math.max(1, Math.round(height * scale));

  const src = document.createElement("canvas");
  src.width = width;
  src.height = height;
  const srcCtx = src.getContext("2d");
  if (!srcCtx) throw new Error("2D context unavailable");
  const imgData = srcCtx.createImageData(width, height);
  imgData.data.set(raster);
  srcCtx.putImageData(imgData, 0, 0);

  const dst = document.createElement("canvas");
  dst.width = tw;
  dst.height = th;
  const dstCtx = dst.getContext("2d");
  if (!dstCtx) throw new Error("2D context unavailable");
  // White backdrop so the thumbnail reads as paper rather than showing the
  // dark panel background through the transparent pixels of erased areas.
  dstCtx.fillStyle = "#ffffff";
  dstCtx.fillRect(0, 0, tw, th);
  dstCtx.imageSmoothingEnabled = true;
  dstCtx.imageSmoothingQuality = "high";
  dstCtx.drawImage(src, 0, 0, tw, th);
  return dst.toDataURL("image/png");
}

/**
 * Decode a PNG data URL back into an RGBA `Uint8ClampedArray`. Resolves with
 * the pixel data plus the decoded dimensions (callers cross-check these
 * against the saved metadata to detect corruption).
 */
export function pngToRaster(
  dataUrl: string,
): Promise<{ data: Uint8ClampedArray; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("2D context unavailable"));
        return;
      }
      ctx.drawImage(img, 0, 0);
      const { data } = ctx.getImageData(
        0,
        0,
        img.naturalWidth,
        img.naturalHeight,
      );
      resolve({ data, width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () =>
      reject(new Error("Failed to decode saved inking raster"));
    img.src = dataUrl;
  });
}
